import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";
import { collection, doc, serverTimestamp, setDoc } from "firebase/firestore";
import { auth, db } from "../firebaseConfig";
import { useEntitlements } from "../hooks/useEntitlements";
import { getToken } from "firebase/app-check";
import { appCheck } from "../firebaseConfig"; // exporta appCheck desde tu config

import "../styles/generalStyle.css";
import "./CreateLyrics.css";

import { useLanguage } from "../i18n/LanguageContext";

// ✅ Pega aquí la URL real de tu función (admin)
const GENERAR_LETRA_URL_ANTES = "https://generarletra-eoq6iazhiq-uc.a.run.app";
const GENERAR_LETRA_URL = "https://us-central1-cancionesia.cloudfunctions.net/generarLetraCheck";


export default function CreateLyrics() {
  const { t } = useLanguage();
  const navigate = useNavigate();

  const [firebaseUser, setFirebaseUser] = useState(() => auth.currentUser);
  const {
    loading: entLoading,
    canLyrics,
    consumeLyricsCredit,
    entitlements
  } = useEntitlements(firebaseUser?.uid);

  // Form
  const [titulo, setTitulo] = useState("");
  const [formato, setFormato] = useState("verso_coro"); // ✅ default: verso y coro
  const [ideas, setIdeas] = useState("");

  // Idioma objetivo de la letra (salida)
  const [targetLang, setTargetLang] = useState("es"); // default español

  const langOptions = useMemo(() => ([
    { code: "es", label: "Español" },
    { code: "en", label: "English" },
    { code: "fr", label: "Français" },
    { code: "de", label: "Deutsch" },
    { code: "pt", label: "Português (Brasil)" }
  ]), []);

  const targetLangLabel = useMemo(() => {
    const found = langOptions.find(x => x.code === targetLang);
    return found?.label || "Español";
  }, [targetLang, langOptions]);


  // Generación
  const [generating, setGenerating] = useState(false);
  const [generatedLetra, setGeneratedLetra] = useState(""); // editable
  const [savedId, setSavedId] = useState(null);

  // UI
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => setFirebaseUser(u || null));
    return () => unsub();
  }, []);

  const ideasLen = ideas.trim().length;

  const overridePrompt = useMemo(() => {
    // Si quieres forzar formato desde el cliente (opcional)
    // Si no quieres override, mándalo como null.
    // Pero esto te permite controlar el output sin tocar el TEMPLATE del backend.
    const isVersos = formato === "solo_versos";
    return isVersos
      ? "Genera una letra SOLO con versos (sin secciones de coro). Usa secciones con corchetes si aplica."
      : "Genera una letra con estructura [verso], [coro], [verso], [coro], [verso], [verso final]. Usa secciones con corchetes.";
  }, [formato]);

  const validateCreate = () => {
    if (!firebaseUser) return t("createLyrics.errors.notLogged");
    if (!titulo.trim()) return t("createLyrics.titleRequiredError");
    if (!formato) return t("createLyrics.formatRequiredError");
    if (ideas.trim().length < 100) return t("createLyrics.ideasRequiredError");
    if (!GENERAR_LETRA_URL || GENERAR_LETRA_URL.includes("PEGAR_AQUI")) {
      return t("createLyrics.errors.missingFunctionUrl");
    }
    return "";
  };

 
const handleGenerate = async () => {
    const msg = validateCreate();
    if (msg) { setError(msg); return; }

    if (!canLyrics) { setError(t("limites.limiteAlcanzadoLetra")); return; }

    try {
      setError("");
      setGenerating(true);
      setGeneratedLetra("");
      setSavedId(null);

      // ✅ 1) App Check token
      const { token } = await getToken(appCheck, false);

      await consumeLyricsCredit();

      // ✅ 2) Arma idea final (esto reemplaza tu "body" duplicado)
      const ideaFinal =
        `${t("createLyrics.promptLangLine", "Crea esta letra en")} ${targetLangLabel}.\n\n` +
        `${t("createLyrics.promptHeader")}\n` +
        `${t("createLyrics.promptTitle")}: ${titulo.trim()}\n` +
        `${t("createLyrics.promptFormat")}: ${
          formato === "solo_versos"
            ? t("createLyrics.formatOnlyVerses")
            : t("createLyrics.formatVerseChorus")
        }\n` +
        `${t("createLyrics.promptIdeas")}:\n${ideas.trim()}`;

      // ✅ 3) Un solo fetch (con App Check)
      const resp = await fetch(GENERAR_LETRA_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Firebase-AppCheck": token,
        },
        body: JSON.stringify({ idea: ideaFinal,  targetLang, formato  }),
      });

      let data = {};
      try { data = await resp.json(); } catch { data = {}; }

      if (!resp.ok) throw new Error(data?.error || t("createLyrics.errors.generateFailed"));

      setGeneratedLetra((data?.letra || "").trim());
    } catch (e) {
      console.error("Error generando letra:", e);
      setError(e?.message || t("createLyrics.errors.generateFailed"));
    } finally {
      setGenerating(false);
    }
  };

  const validateSave = () => {
    if (!firebaseUser) return t("createLyrics.errors.notLogged");
    if (!titulo.trim()) return t("createLyrics.titleRequiredError");
    if (!generatedLetra.trim()) return t("createLyrics.errors.emptyLyrics");
    return "";
  };

  const handleSave = async () => {
    const msg = validateSave();
    if (msg) {
      setError(msg);
      return;
    }

    try {
      setSaving(true);
      setError("");

      const letrasCol = collection(db, "usuarios", firebaseUser.uid, "letras");
      const docRef = doc(letrasCol);

      const letrasData = {
        id: docRef.id,
        userId: firebaseUser.uid,
        titulo: titulo.trim(),
        letra: generatedLetra,
        fechaCreacion: serverTimestamp()
      };

      await setDoc(docRef, letrasData, { merge: true });
      setSavedId(docRef.id);
    } catch (e) {
      console.error("Error guardando letra:", e);
      setError(t("createLyrics.errors.saveFailed"));
    } finally {
      setSaving(false);
    }
  };

  if (!firebaseUser) {
    return (
      <div className="page">
        <div className="container">
          <div className="card">
            <h1 className="h1">{t("createLyrics.notLoggedTitle")}</h1>
            <p className="p-muted" style={{ marginTop: 8 }}>
              {t("createLyrics.notLoggedText")}
            </p>
            <button className="btn btn-primary" style={{ marginTop: 14 }} onClick={() => navigate("/login")}>
              {t("createLyrics.goToLogin")}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page">
      <div className="container">
        <div className="grid-2">
          {/* FORM */}
          <section className="card">
            <h1 className="h1">{t("createLyrics.title")}</h1>
            <p className="p-muted" style={{ marginTop: 8 }}>
              {t("createLyrics.subtitle")}
            </p>

            <div className="form" style={{ marginTop: 10 }}>
              <label className="label">
                {t("createLyrics.songTitleLabel")} *
                <input
                  className="input"
                  value={titulo}
                  onChange={(e) => setTitulo(e.target.value)}
                  placeholder={t("createLyrics.songTitlePlaceholder")}
                  maxLength={80}
                />
              </label>

              <label className="label">
                {t("createLyrics.outputLanguageLabel", "Idioma de la letra")} *
                <select
                  className="input"
                  value={targetLang}
                  onChange={(e) => setTargetLang(e.target.value)}
                >
                  {langOptions.map((o) => (
                    <option key={o.code} value={o.code}>
                      {o.label}
                    </option>
                  ))}
                </select>

                <span className="help-text">
                  {t(
                    "createLyrics.outputLanguageHelp",
                    "La letra se generará en el idioma que elijas."
                  )}
                </span>
              </label>


              <div className="label">
                {t("createLyrics.formatLabel")} *
                <div className="formatRow">
                  <button
                    type="button"
                    className={`formatChip ${formato === "solo_versos" ? "active" : ""}`}
                    onClick={() => setFormato("solo_versos")}
                  >
                    {t("createLyrics.formatOnlyVerses")}
                  </button>

                  <button
                    type="button"
                    className={`formatChip ${formato === "verso_coro" ? "active" : ""}`}
                    onClick={() => setFormato("verso_coro")}
                  >
                    {t("createLyrics.formatVerseChorus")}
                  </button>
                </div>
                <span className="help-text">{t("createLyrics.formatHelp")}</span>
              </div>

              <label className="label">
                {t("createLyrics.ideasLabel")} * ({t("createLyrics.ideasMin")})
                <textarea
                  className="input textarea"
                  value={ideas}
                  onChange={(e) => setIdeas(e.target.value)}
                  placeholder={t("createLyrics.ideasPlaceholder")}
                  rows={6}
                />
                <span className={`help-text ${ideasLen < 100 ? "warn" : ""}`}>
                  {t("createLyrics.ideasCounter", { count: ideasLen })}
                </span>
              </label>

              {error ? <div className="alert-error">{error}</div> : null}

              <button
                type="button"
                className="btn btn-primary"
                disabled={generating || saving || entLoading || !canLyrics}
                onClick={handleGenerate}
              >
                {generating ? t("createLyrics.generatingButton") : t("createLyrics.generateButton")}
              </button>

              {!entLoading && !canLyrics ? (
                  <div className="alert-error" style={{ marginTop: 10 }}>
                    {t("limites.limiteAlcanzadoLetra")}
                  </div>
                ) : null}
                
              <p className="note">{t("createLyrics.note")}</p>
            </div>
          </section>

          {/* RESULT */}
          <section className="card">
            <h2 className="h2">{t("createLyrics.resultTitle")}</h2>
            <p className="p-muted">{t("createLyrics.resultSubtitle")}</p>

            {generating ? (
              <div className="loading" style={{ marginTop: 16 }}>
                <div className="spinner" />
                <p style={{ margin: 0 }}>{t("createLyrics.generatingText")}</p>
              </div>
            ) : (
              <>
                <label className="label" style={{ marginTop: 12 }}>
                  {t("createLyrics.lyricsLabel")}
                  <textarea
                    className="input textarea"
                    value={generatedLetra}
                    onChange={(e) => setGeneratedLetra(e.target.value)}
                    placeholder={t("createLyrics.lyricsPlaceholder")}
                    rows={10}
                  />
                  <span className="help-text">{t("createLyrics.lyricsHelp")}</span>
                </label>

                <button
                  type="button"
                  className="btn btn-success"
                  disabled={saving || (generatedLetra?.trim().length || 0) < 100}
                  onClick={handleSave}
                >
                  {saving ? t("createLyrics.savingButton") : t("createLyrics.saveButton")}
                </button>

                {(generatedLetra?.trim().length || 0) < 100 ? (
                  <div className="help-text warn">{t("createLyrics.minChars100")}</div>
                ) : null}
               
                {savedId ? (
                  <div className="savedBox">
                    <div className="savedRow">
                      <span className="savedLabel">{t("createLyrics.savedOk")}</span>
                      <span className="savedValue">{savedId}</span>
                    </div>

                    <button
                      type="button"
                      className="btn"
                      style={{ marginTop: 10 }}
                      onClick={() => navigate("/app/letras")}
                    >
                      {t("createLyrics.goToMyLyrics")}
                    </button>
                  </div>
                ) : null}
              </>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}
